// Google Ads click-id attribution, shared by the email signup form (SignUp.vue)
// and the Google / OAuth signup flow (GoogleAuthButton.vue). The castr.com edge
// middleware stores the ad click id (gclid/gbraid/wbraid) in a first-party
// `castr_attr` cookie on the landing page; we forward it to the backend so it can
// be persisted against the user for offline conversion tracking. Every lookup is
// defensive and SSR-safe — attribution must never break auth, so anything
// unexpected resolves to '' (a no-op downstream).

// First-party `castr_attr` cookie, already type-tagged by the edge middleware.
export function readAttributionCookie() {
	try {
		if (typeof document === 'undefined') return ''
		const match = document.cookie.match(/(?:^|;\s*)castr_attr=([^;]*)/)
		return match ? decodeURIComponent(match[1]) : ''
	} catch {
		return ''
	}
}

// Resolve the ad click id with fallbacks for returning / cross-session converters
// who didn't re-trigger the castr.com edge middleware this session, but whom
// Google still attributes via its own 90-day click cookie. Order: `castr_attr`
// cookie → click id in the current URL → Google's `_gcl_aw` linker cookie. The
// type tag downstream expects is preserved: `gclid` stays a plain value;
// `gbraid`/`wbraid` are tagged `gbraid:VALUE` / `wbraid:VALUE`. Safe no-op when
// nothing is present.
export function resolveAttribution() {
	// 1) First-party `castr_attr` cookie (already type-tagged by the edge middleware).
	const cookie = readAttributionCookie()
	if (cookie) return cookie
	try {
		// 2) Click id in the current URL (Google auto-tagging on this landing).
		if (typeof window !== 'undefined' && window.location) {
			const params = new URLSearchParams(window.location.search)
			const gclid = params.get('gclid')
			if (gclid) return gclid
			const gbraid = params.get('gbraid')
			if (gbraid) return `gbraid:${gbraid}`
			const wbraid = params.get('wbraid')
			if (wbraid) return `wbraid:${wbraid}`
		}
		// 3) Google's linker cookie `_gcl_aw` (format `GCL.<timestamp>.<gclid>`).
		if (typeof document !== 'undefined') {
			const m = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]*)/)
			if (m) {
				const gclid = decodeURIComponent(m[1]).split('.').slice(2).join('.')
				if (gclid) return gclid
			}
		}
	} catch {
		/* attribution must never break auth — fall through to no-op */
	}
	return ''
}
